import { useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import useRoutes from 'hooks/use-routes';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useOrderContext } from 'state/order';
import { ServiceMode } from 'state/service-mode';
import { routes } from 'utils/routing';

import useCheckServiceMode from './use-check-service-mode';

/**
 * This query param allows us to link to the store-locator
 * and default the user to a service mode, currently only TAKEOUT.
 * `?sm=takeout
 *
 * The value of the param is case insensitive. 'TAKEOUT' == 'takeout'.
 */
export const SET_SERVICE_MODE_QUERY_PARAM_STORE_LOCATOR = 'sm';
const VALID_SERVICE_MODE_QUERY_VALUES: string[] = [ServiceMode.TAKEOUT];

export default function useStoreLocatorRedirects() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setStoreLocatorCallbackUrl } = useLocationContext();
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  // TODO: remove this whole hook once new UI feature rolls out
  //       or figure out a better way to decouple this beforehand
  const enableLocatorUIRefresh = useFlag(LaunchDarklyFlag.ENABLE_LOCATOR_UI_REFRESH);
  const order = useOrderContext();
  const { isPermissionKnown } = useGeolocation();

  // custom hook to allow brand specific flow control
  const checkServiceMode = useCheckServiceMode();

  const { getLocalizedRouteForPath } = useRoutes();

  /*
   * This effect handles our redirect logic to make sure the proper modal
   * is displayed to the user to ensure we gather serviceMode and address
   * info in order to show a proper map, or find the accurate store.
   */
  useEffect(() => {
    if (location.pathname !== getLocalizedRouteForPath('store-locator') || enableLocatorUIRefresh) {
      return;
    }

    // allow for brand specific control via "checkServiceMode"
    // ie. TH doesn't want to show the user the delivery/pickup modal
    if (checkServiceMode && !order.serviceMode && enableOrdering) {
      // check for service mode selection via query param
      const searchParams = new URLSearchParams(location.search);
      // retrieve service mode query param value and UPPERCASE it
      const paramServiceMode = (
        searchParams.get(SET_SERVICE_MODE_QUERY_PARAM_STORE_LOCATOR) || ''
      ).toUpperCase();

      if (VALID_SERVICE_MODE_QUERY_VALUES.includes(paramServiceMode)) {
        order.selectServiceMode(paramServiceMode);
      } else {
        navigate(routes.serviceMode, { replace: true });
        return;
      }
    }

    if (!isPermissionKnown) {
      navigate(routes.locationService, { replace: true });
      return;
    }

    //reset callback url on unmount
    return () => setStoreLocatorCallbackUrl('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermissionKnown]);
}
